import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";

import { setGuideMeBypass } from "@/common/api/httpClient";
import { useAuth } from "@/common/hooks/useAuth";
import { useToast } from "@/common/hooks/useToast";
import {
  startGuideMePreview,
  endGuideMePreview,
} from "@/common/services/guide-me/guide-me-preview.api";
import { TOUR_STEPS, type Tour, type TourStep } from "@/pages/guideme/data";
import { TourOverlay } from "@/pages/guideme/TourOverlay";

/**
 * Tours in this set operate on the user's REAL organization (creating a real
 * project, inviting a real teammate) and must never switch the active
 * project to Sample Project.
 */
const NO_PREVIEW_TOUR_IDS = new Set([
  "create-project",
  "invite-members",
  "tour-dashboard",
  "user-profile",
]);

type CompletedSignal = { id: string; ts: number };
type ClosedSignal = { id: string; step: number; total: number; ts: number };

type GuideTourCtx = {
  activeTour: Tour | null;
  seedingTourId: string | null;
  lastCompletedTourId: string | null;
  lastCompletedSignal: CompletedSignal | null;
  lastClosedSignal: ClosedSignal | null;
  openTour: (t: Tour, restart?: boolean) => void;
  closeTour: (step: number) => void;
  completeTour: () => void;
};

const Ctx = createContext<GuideTourCtx>({
  activeTour: null,
  seedingTourId: null,
  lastCompletedTourId: null,
  lastCompletedSignal: null,
  lastClosedSignal: null,
  openTour: () => {},
  closeTour: () => {}, // step param intentionally ignored in default
  completeTour: () => {},
});

/** Placeholder shown for tours whose steps haven't been authored yet. */
function buildComingSoonSteps(tour: Tour): TourStep[] {
  return [
    {
      title: `Welcome to ${tour.title}`,
      description: `This interactive tour is coming soon. In the meantime, explore the feature at ${tour.route} to get familiar.`,
      eta: "1 minute",
      selector: undefined,
      placement: undefined,
    },
  ];
}

function resolveSteps(tour: Tour): TourStep[] {
  return TOUR_STEPS[tour.id] ?? buildComingSoonSteps(tour);
}

export function GuideTourProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { userPreferenceData, refetchUserPreference } = useAuth();
  const { showToast } = useToast();
  const [activeTour, setActiveTour] = useState<Tour | null>(null);
  // Busy while a preview-session switch (start/end) is in flight. Kept under
  // its original name since GuideMeWorkspace's UI already reads it as a
  // "preparing the tour" loading flag.
  const [seedingTourId, setSeedingTourId] = useState<string | null>(null);
  const [lastCompletedTourId, setLastCompletedTourId] = useState<string | null>(null);
  const [lastCompletedSignal, setLastCompletedSignal] = useState<CompletedSignal | null>(null);
  const [lastClosedSignal, setLastClosedSignal] = useState<ClosedSignal | null>(null);
  const openingTourRef = useRef(false);
  // Tracks the step index each tour was closed at so "Continue" resumes there.
  const closedStepRef = useRef<Record<string, number>>({});
  // True while the active tour is previewing the org's Sample Project (i.e. it
  // isn't one of NO_PREVIEW_TOUR_IDS). Drives whether exit paths must call
  // endGuideMePreview to restore the user's real active project.
  const previewActiveRef = useRef(false);

  const endPreviewIfActive = useCallback(() => {
    if (!previewActiveRef.current) return;
    previewActiveRef.current = false;
    endGuideMePreview()
      .catch(() => {
        /* best-effort — the server-side sweep job cleans up stale sessions */
      })
      .finally(() => {
        refetchUserPreference();
      });
  }, [refetchUserPreference]);

  // Best-effort cleanup if the tab is closed/refreshed mid-tour: the sweep job
  // is the real safety net (TTL-bounded), but sendBeacon gives a much faster
  // restore for the common "closed the tab" case. Cookies (withCredentials)
  // are sent automatically with sendBeacon, same as a normal request.
  useEffect(() => {
    const beaconEnd = () => {
      if (!previewActiveRef.current) return;
      previewActiveRef.current = false;
      try {
        const base = `${window.location.origin}`;
        navigator.sendBeacon?.(
          `${base}/api/guideme/preview/end`,
          new Blob([], { type: "application/json" }),
        );
      } catch {
        /* ignore — sweep job will clean up */
      }
    };
    window.addEventListener("pagehide", beaconEnd);
    return () => window.removeEventListener("pagehide", beaconEnd);
  }, []);

  // While a tour is active or its preview is being prepared, attach the
  // guide-me bypass header to all API calls so "[Guide Me]"-prefixed Sample
  // Project rows (e.g. the canonical smoke-test cycle, or anything the user
  // creates during a teach-by-creating step) are visible in normal lists —
  // the backend hides them everywhere else. Cleared when the tour ends.
  useEffect(() => {
    setGuideMeBypass(activeTour !== null || seedingTourId !== null);
    return () => setGuideMeBypass(false);
  }, [activeTour, seedingTourId]);

  const openTour = useCallback(
    async (t: Tour, restart = false) => {
      if (openingTourRef.current || seedingTourId || activeTour) return;
      if (restart) {
        delete closedStepRef.current[t.id];
      }
      openingTourRef.current = true;

      const needsPreview = !NO_PREVIEW_TOUR_IDS.has(t.id);
      const orgId: number | undefined = userPreferenceData?.data?.activeOrganizationId;

      try {
        if (needsPreview) {
          if (!orgId) {
            showToast({
              id: "tour-preview-no-org",
              title: "No active organization",
              description: "Select an organization before starting this tour.",
              variant: "warning",
            });
            return;
          }
          setSeedingTourId(t.id);
          setGuideMeBypass(true);
          try {
            await startGuideMePreview(orgId);
            previewActiveRef.current = true;
            // Pull the server's new active version/project so project-scoped
            // queries refetch with the Sample Project scope before we navigate.
            await refetchUserPreference();
          } catch (err: any) {
            showToast({
              id: "tour-preview-error",
              title: "Couldn't start the tour",
              description:
                err?.response?.data?.error ??
                "Could not switch to the Sample Project. Please try again.",
              variant: "destructive",
            });
            return;
          } finally {
            setSeedingTourId(null);
          }
        }

        // Navigate to the route for the step we're starting at.
        // For continue (not restart): resume at the saved step index.
        const steps = TOUR_STEPS[t.id];
        const resumeIdx = restart ? 0 : (closedStepRef.current[t.id] ?? 0);
        // Walk back from resumeIdx to find the nearest step that declares a route.
        let targetRoute: string | undefined;
        for (let k = resumeIdx; k >= 0; k--) {
          if (steps?.[k]?.route) {
            targetRoute = steps[k].route;
            break;
          }
        }
        if (!targetRoute && (!steps || steps.length === 0)) targetRoute = t.route;
        if (targetRoute) navigate(targetRoute);
        setActiveTour(t);
      } finally {
        openingTourRef.current = false;
      }
    },
    [activeTour, navigate, seedingTourId, userPreferenceData, showToast, refetchUserPreference],
  );

  const tourSteps = useMemo(
    () => (activeTour ? resolveSteps(activeTour) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeTour?.id],
  );

  const closeTour = useCallback(
    (step: number) => {
      if (activeTour) {
        closedStepRef.current[activeTour.id] = step;
        // Emit a signal so GuideMeWorkspace can store real step-based progress.
        setLastClosedSignal({
          id: activeTour.id,
          step,
          total: tourSteps.length,
          ts: Date.now(),
        });
      }
      endPreviewIfActive();
      setActiveTour(null);
    },
    [activeTour, tourSteps.length, endPreviewIfActive],
  );

  const completeTour = useCallback(() => {
    if (activeTour) {
      setLastCompletedTourId(activeTour.id);
      // Use a new object every time so the useEffect in GuideMeWorkspace fires
      // even when the same tour is completed more than once (same string = no update).
      setLastCompletedSignal({ id: activeTour.id, ts: Date.now() });
      navigate("/guideme");
    }
    endPreviewIfActive();
    setActiveTour(null);
  }, [activeTour, navigate, endPreviewIfActive]);

  return (
    <Ctx.Provider
      value={{
        activeTour,
        seedingTourId,
        lastCompletedTourId,
        lastCompletedSignal,
        lastClosedSignal,
        openTour,
        closeTour,
        completeTour,
      }}
    >
      {children}
      {activeTour && (
        <TourOverlay
          key={activeTour.id}
          tourTitle={activeTour.title}
          steps={tourSteps}
          initialStep={closedStepRef.current[activeTour.id] ?? 0}
          onClose={closeTour}
          onComplete={completeTour}
          navigate={navigate}
        />
      )}
    </Ctx.Provider>
  );
}

export function useGuideTour() {
  return useContext(Ctx);
}
